import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

const WsContext = createContext(null);

const EVENTS_LS_KEY = 'ws_events_v1';

export function WsProvider({ children }) {
  const wsRef = useRef(null);
  const retryRef = useRef(0);

  const [isConnected, setIsConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);

  // ✅ load events from localStorage
  const [events, setEvents] = useState(() => {
    try {
      const raw = localStorage.getItem(EVENTS_LS_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });

  // ✅ persist events to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(EVENTS_LS_KEY, JSON.stringify(events || []));
    } catch {
      // ignore
    }
  }, [events]);

  const WS_URL = useMemo(() => {
    const host = window.location.hostname;
    return `ws://${host}:3000/ws/messages`;
  }, []);

  const connect = () => {
    if (wsRef.current && (wsRef.current.readyState === 0 || wsRef.current.readyState === 1)) return;

    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;

    ws.onopen = () => {
      retryRef.current = 0;
      setIsConnected(true);
    };

    ws.onmessage = (evt) => {
      try {
        const raw = JSON.parse(evt.data);
        if (raw?.type === 'connected') return;

        setLastEvent(raw);

        setEvents((prev) => {
          const next = [raw, ...(Array.isArray(prev) ? prev : [])];
          if (next.length > 200) next.length = 200; // boleh 50 kalau mau, ini biar notif gak cepat hilang
          return next;
        });
      } catch {
        // ignore parse errors
      }
    };

    ws.onclose = () => {
      setIsConnected(false);

      const retry = retryRef.current + 1;
      retryRef.current = retry;
      const delay = Math.min(1000 * Math.pow(2, Math.min(retry, 5)), 15000);

      setTimeout(() => {
        connect();
      }, delay);
    };

    ws.onerror = () => {
      // onclose handle retry
    };
  };

  useEffect(() => {
    connect();
    return () => {
      try {
        wsRef.current?.close();
      } catch {}
      wsRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [WS_URL]);

  const clearEvents = () => {
    setEvents([]);
    try {
      localStorage.removeItem(EVENTS_LS_KEY);
    } catch {}
  };

  const value = useMemo(
    () => ({
      ws: wsRef,
      isConnected,
      lastEvent,
      events,
      clearEvents
    }),
    [isConnected, lastEvent, events]
  );

  return <WsContext.Provider value={value}>{children}</WsContext.Provider>;
}

export function useWs() {
  const ctx = useContext(WsContext);
  if (!ctx) throw new Error('useWs must be used inside <WsProvider />');
  return ctx;
}
